<template>
    <div
        class="relative flex items-center rounded-02 focus-within:border-primary-3 transition-colors"
        :class="[searchBarStyle, placementClass]"
        @click="handleContainerClick"
    >
        <span class="absolute right-2 z-1 text-content-basic-3"
            style="z-index: 1;"
        >
            <calendar width="20" height="20" color="text-content-basic-3" weight="REGULAR"/>
        </span>
        <div class="flex-1 w-full">
            <Datepicker
                ref="datepickerRef"
                :class="datepickerClasses"
                input-class="border-none outline-none bg-transparent w-full h-full"
                :placeholder="placeholder"
                :disabled="disabled"
                :weekStartsOn="0"
                :disabled-dates="disabledDates" 
                :lowerLimit="lowerLimit"
                :upperLimit="upperLimit"
                v-model="dateValue"
                :inputFormat="displayFormat"
            />
        </div>
    </div>
</template>

<script>
import Datepicker from 'vue3-datepicker';
import moment from 'moment-timezone';
import calendar from './icon/calendar-today.vue';

export default {
    components : {
        Datepicker, 
        calendar
    },
    props:{
        error : { type : Boolean, default : false }, 
        placeholder : { type : String, default : 'MM/DD/YYYY' },
        searchSize : { type : String , default : 'LG' },
        disabled: { type: Boolean, default: false },
        modelValue: { type : [Date, String] }, // "" 인 경우 위해 
        lowerLimit: Date,
        upperLimit: Date,
        format: { type: String, default: 'YYYYMMDD' },
        output: { type: String, default: '' },
        displayFormat: { type: String, default: 'MM/dd/yyyy' },
        disableWeekends: {
            type: Array, // [0, 6] 이런 식
            default: () => [],
        },
        menuPlacement: {
            type: String,
            default: 'bottom', // 'top' | 'bottom'
            validator: (v) => ['top', 'bottom', 'left'].includes(v),
        },
    },
    emits: ['update:modelValue', 'changeDate', 'update:output'],
    data(){
        return {
            sizeMap: {
                LG: 'h-[2.75rem] font-normal text-sm leading-none text-content-basic-1',
                SM: 'h-[2.25rem] font-normal text-xs leading-3 text-content-basic-1',
            },
        }
    },
    computed:{
        searchBarStyle(){
            const size = String(this.searchSize).toUpperCase();
            const sizeClass = this.sizeMap[size] || '';

            const borderClass = this.error
                ? 'border border-accent-red-3'
                : 'border border-border-basic-2';

            const stateClass = this.disabled
                ? 'bg-background-basic-3 text-content-basic-2 cursor-not-allowed'
                : 'bg-background-basic-1 text-content-basic-1';

            return [sizeClass, borderClass, stateClass]
                .filter(Boolean)
                .join(' ');
        },
        datepickerClasses() {
            // 내부 인풋은 테두리/배경 없이, 바깥 래퍼 스타일만 사용
            return [
                'block w-full h-full',
                'pl-3 pr-8 py-2', // 좌측 여백 + 아이콘 공간
                'bg-transparent',
                'outline-none',
                'text-sm',
                'placeholder:text-content-basic-3',
            ].join(' ');
        },
        placementClass() {
            // return  `datepicker--left`;
            return  `datepicker--${this.menuPlacement}`;
        },
        dateValue: {
            get() {
                const self = this;
                if (!self.modelValue) return null;

                const date = self.getDateMap(self.modelValue);
                const parsedDate = moment().year(date.year).month(date.month).date(date.day);

                self.$emit('update:output', parsedDate.format(self.format));

                return parsedDate.toDate();
            },
            set(newDate) {
                const self = this;

                const date = self.getDateMap(newDate);
                const parsedDate = moment().year(date.year).month(date.month).date(date.day);

                self.$emit('update:modelValue', parsedDate.toDate());
                self.$emit('update:output', parsedDate.format(self.format));
                self.$emit('changeDate', newDate);
            },
        },
        disabledDates() {
            if (!Array.isArray(this.disableWeekends)) {
                return {};
            }

            return {
                predicate: (date) => {
                    const day = date.getDay();
                    return this.disableWeekends.includes(day);
                },
            };
        },
    },
    methods: {
        getDateMap(dateObject) {
            if (!dateObject) return {};
            const year = dateObject.getFullYear();
            const month = String(dateObject.getMonth()).padStart(2, '0');
            const day = String(dateObject.getDate()).padStart(2, '0');
            return { year, month, day };
        },
        changTargetDate(changedDate) {
            if(!changedDate){
                return;
            }
            this.dateValue = changedDate;
        },
        handleContainerClick(event){
            if (this.disabled) {
                return;
            }

            const isInputClick = event?.target?.closest && event.target.closest('input');
            const isPopupClick = event?.target?.closest && event.target.closest('.v3dp__popout');

            if (isInputClick || isPopupClick) {
                return;
            }

            this.focusDatepickerInput();
        },
        focusDatepickerInput(){
            const inputEl = this.$refs.datepickerRef?.$el?.querySelector('input');
            if (!inputEl) {
                return;
            }
            inputEl.focus();
            inputEl.click();
        }
    },
}

</script>

<style scoped>
/* Datepicker 팝업 위치 */
.datepicker--bottom :deep(.v3dp__popout) {
    top: calc(100% + 4px);
    bottom: auto;
    left: 0;
}

.datepicker--top :deep(.v3dp__popout) {
    bottom: calc(100% + 4px);
    top: auto;
    left: 0;
}

.datepicker--left :deep(.v3dp__popout) {
    top: calc(100% + 4px);
    right: 0;
}

/* 내부 vue3-datepicker 인풋/래퍼 윤곽 제거 */
:deep(.v3dp__datepicker) {
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
}

:deep(.v3dp__input_wrap) {
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
}

:deep(.v3dp__input) {
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    outline: none !important;
}

:deep(input) {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
}
</style>
